import { Command } from "commander";
import { writeFileSync, mkdirSync, existsSync } from "node:fs";
import { resolve, join } from "node:path";
import chalk from "chalk";
import { createStoreFromConfig } from "@fathom/store";
import { generateReport } from "@fathom/reports";
import type { ReportConfig } from "@fathom/reports";
import { getProjectName } from "../project-config.js";
import { fatalError } from "../ux/errors.js";

export const reportCommand = new Command("report")
  .description("Generate a shareable project report")
  .option("-p, --project <name>", "Project name")
  .option(
    "-f, --format <format>",
    "Output format: markdown, html, json",
    "markdown",
  )
  .option("-o, --output <path>", "Output file path")
  .option("--no-features", "Exclude feature details")
  .option("--no-costs", "Exclude cost breakdown")
  .option("--no-velocity", "Exclude velocity metrics")
  .option("--no-accuracy", "Exclude estimate accuracy")
  .option("--days <n>", "Limit to last N days", parseInt)
  .action(
    async (options: {
      project?: string;
      format: string;
      output?: string;
      features: boolean;
      costs: boolean;
      velocity: boolean;
      accuracy: boolean;
      days?: number;
    }) => {
      const format = options.format as "markdown" | "html" | "json";
      if (!["markdown", "html", "json"].includes(format)) {
        fatalError(
          `Invalid format "${options.format}"`,
          "Supported formats are markdown, html, and json",
          "Use: fathom report --format markdown|html|json",
        );
      }

      const projectName = options.project ?? getProjectName();

      // Get store
      const store = await createStoreFromConfig();

      // Look up project
      const project = await store.getProject(projectName);
      const projectId = project?.id ?? projectName;

      // Build date range
      let dateRange: { start: number; end: number } | undefined;
      if (options.days) {
        const now = Date.now();
        dateRange = {
          start: now - options.days * 86400000,
          end: now,
        };
      }

      const config: ReportConfig = {
        projectName,
        projectId,
        dateRange,
        includeFeatureDetails: options.features,
        includeCostBreakdown: options.costs,
        includeVelocityMetrics: options.velocity,
        includeEstimateAccuracy: options.accuracy,
        format,
      };

      console.log(chalk.bold(`\nFathom \u2014 Project Report: ${projectName}`));
      console.log(chalk.dim("\u2500".repeat(40)));
      console.log(chalk.dim("  Collecting data..."));

      try {
        const report = await generateReport(store, config);

        // Determine output path
        const ext = format === "markdown" ? "md" : format;
        const today = new Date().toISOString().split("T")[0];
        const defaultPath = join(
          process.cwd(),
          ".fathom",
          "reports",
          `${projectName}-${today}.${ext}`,
        );
        const outputPath = options.output
          ? resolve(options.output)
          : defaultPath;

        // Ensure directory exists
        const dir = resolve(outputPath, "..");
        if (!existsSync(dir)) {
          mkdirSync(dir, { recursive: true });
        }

        writeFileSync(outputPath, report, "utf-8");

        console.log(
          chalk.green(`\n  \u2713 Report generated: ${outputPath}`),
        );
      } catch (err) {
        fatalError(
          "Report generation failed",
          (err as Error).message,
          "Check that .fathom/ has tracking data and the store is accessible",
        );
      }
    },
  );
